<script setup lang="ts">
import { computed } from 'vue';

import { InstallationRiskApi } from '~/services/installation-risk-api/installation-risk-api.types';

import EquipmentTable from './EquipmentTable.vue';

interface Props {
  data: InstallationRiskApi.HardwareForm;
}

const props = defineProps<Props>();

const systemTypeLabel = computed(() => (props.data.system_type === 'with_battery' ? 'Com Bateria' : 'Sem Bateria'));

const hasInverters = computed(() => props.data.inverters && props.data.inverters.length > 0);
const hasMicroinverters = computed(
  () => props.data.microinverters && props.data.microinverters.length > 0,
);
const hasModules = computed(() => props.data.modules && props.data.modules.length > 0);
const hasBatteries = computed(() => props.data.batteries && props.data.batteries.length > 0);

const inverterTypeLabel = computed(() => {
  if (!props.data.inverter_type) return '';
  return `Normal (${props.data.inverter_type})`;
});

const invertersFormatted = computed(() => {
  if (!props.data.inverters?.length) return [];
  return props.data.inverters.map((inverter) => ({
    ...inverter,
    power: inverter.power == null ? '-' : `${(inverter.power).toFixed(2)} kW`,
    total_power: inverter.total_power == null ? '-' : `${(inverter.total_power / 1000).toFixed(2)} kW`,
  }));
});

const microinvertersFormatted = computed(() => {
  if (!props.data.microinverters?.length) return [];
  return props.data.microinverters.map((microinverter) => ({
    ...microinverter,
    power: microinverter.power == null ? '-' : `${(microinverter.power).toFixed(2)} kW`,
    total_power: microinverter.total_power == null ? '-' : `${(microinverter.total_power / 1000).toFixed(2)} kW`,
  }));
});

const modulesFormatted = computed(() => {
  if (!props.data.modules?.length) return [];
  return props.data.modules.map((module) => ({
    ...module,
    power: module.power == null ? '-' : `${module.power.toFixed(2)} Wp`,
    total_power: module.total_power == null ? '-' : `${(module.total_power / 1000).toFixed(2)} kWp`,
  }));
});

const batteriesFormatted = computed(() => {
  if (!props.data.batteries?.length) return [];
  return props.data.batteries.map((battery) => ({
    ...battery,
    power: battery.power == null ? '-' : `${battery.power.toFixed(2)} Wp`,
    total_power: battery.total_power == null ? '-' : `${battery.total_power.toFixed(2)} Wp`,
  }));
});
</script>

<template>
  <div class="equipment-details">
    <div class="mb-4">
      <h4 class="section-title">Informações do Sistema</h4>
      <div class="datagrid">
        <div class="datagrid-item">
          <div class="datagrid-title">Tipo de Sistema</div>
          <div class="datagrid-content">
            <span class="badge bg-primary-lt">{{ systemTypeLabel }}</span>
          </div>
        </div>

        <div v-if="data.inverter_type" class="datagrid-item">
          <div class="datagrid-title">Tipo de Inversor</div>
          <div class="datagrid-content">{{ inverterTypeLabel }}</div>
        </div>

        <div v-if="data.distributor" class="datagrid-item">
          <div class="datagrid-title">Distribuidora</div>
          <div class="datagrid-content">
            {{ data.distributor.name }} - {{ data.distributor.state }}
          </div>
        </div>

        <div v-if="data.phase" class="datagrid-item">
          <div class="datagrid-title">Fase</div>
          <div class="datagrid-content">{{ data.phase.name }}</div>
        </div>

        <div v-if="data.has_solfacil_smart !== undefined" class="datagrid-item">
          <div class="datagrid-title">Solfácil Smart</div>
          <div class="datagrid-content">{{ data.has_solfacil_smart ? 'Sim' : 'Não' }}</div>
        </div>
      </div>
    </div>

    <div v-if="hasInverters || hasMicroinverters" class="mt-4">
      <h4 class="section-title">
        <i class="ti ti-bolt" />
        {{ hasInverters ? 'Inversores' : 'Microinversores' }}
        <span class="count-badge">
          {{ data.inverters?.length || data.microinverters?.length }}
        </span>
      </h4>
      <EquipmentTable :data="invertersFormatted.length ? invertersFormatted : microinvertersFormatted" />
    </div>

    <div v-if="hasModules" class="mt-4">
      <h4 class="section-title">
        <i class="ti ti-solar-panel" />
        Módulos
        <span class="count-badge">{{ data.modules?.length }}</span>
      </h4>
      <EquipmentTable :data="modulesFormatted" />
    </div>

    <div v-if="hasBatteries" class="mt-4">
      <h4 class="section-title">
        <i class="ti ti-battery" />
        Baterias
        <span class="count-badge">{{ data.batteries?.length }}</span>
      </h4>
      <EquipmentTable :data="batteriesFormatted" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.equipment-details {
  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    background: var(--bs-primary-bg-subtle);
    color: var(--bs-primary);
    min-width: 1.5rem;
  }
}
</style>
